import { usePagination } from '@ltvco/refresh-lib/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  RECALLS_PILLS,
  type RecallsPill,
  type RecallsSortValue,
  getRecallsSortOptions,
} from './constants';
import { buildSearchParams, hasActiveSearch } from './searchParamBuilders';
import {
  useCpscRecalls,
  useFdaEnforcementRecalls,
  useNewestRecalls,
  useUsdaRecalls,
  useVehicleRecalls,
} from './useRecallsApi';
import {
  buildRecallReportUrl,
  buildResultsPayload,
  normalizeRecallsForCards,
  sortRecallsCards,
} from './utils';

import type { NormalizedRecallCard, RecallsSearchParams } from './interfaces';
import type { RecallsFormValues } from './types';
import type { VehicleFormData } from '../../../SearchHub/Vehicle/utils';

export type ExpandedSection = 'fdaAndUsda' | 'cpsc';

const EXPANDED_SECTION_TITLES: Record<ExpandedSection, string> = {
  fdaAndUsda: 'New Food, Drug, and Medical Device Recalls',
  cpsc: 'New Consumer Product Recalls',
};

const RECALLS_PER_PAGE = 6;

export function useRecallsSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [urlSearchParams] = useSearchParams();
  const initialPill = (() => {
    const fromUrl = urlSearchParams.get('recallPill');
    return RECALLS_PILLS.includes(fromUrl as RecallsPill)
      ? (fromUrl as RecallsPill)
      : 'Vehicle';
  })();
  const [activePill, setActivePill] = useState<RecallsPill>(initialPill);
  const [searchValueByPill, setSearchValueByPill] = useState<
    Partial<Record<RecallsPill, string>>
  >({});
  const [savedDropdownByPill, setSavedDropdownByPill] = useState<
    Partial<
      Record<
        RecallsPill,
        { searchType: string; initialValues?: Partial<VehicleFormData> }
      >
    >
  >({});
  const [searchParams, setSearchParams] = useState<RecallsSearchParams | null>(
    null
  );
  const [expandedSection, setExpandedSection] =
    useState<ExpandedSection | null>(null);
  const [sortOption, setSortOption] = useState<string>('date_desc');

  const searchValue = searchValueByPill[activePill] ?? '';

  // Capture URL params once on mount to auto-trigger search from first-record-search redirect
  const initialUrlSearchParams = useRef(urlSearchParams);
  useEffect(() => {
    const params = initialUrlSearchParams.current;
    const recallSearchType = params.get('recallSearchType');
    if (!recallSearchType) return;

    const fromUrl = params.get('recallPill');
    const pill = RECALLS_PILLS.includes(fromUrl as RecallsPill)
      ? (fromUrl as RecallsPill)
      : 'Vehicle';

    const formValues: RecallsFormValues = { searchType: recallSearchType };

    let initialValues: Partial<VehicleFormData> = {};

    if (recallSearchType === 'VIN') {
      const vin = params.get('vin');
      if (!vin) return;
      formValues.vin = vin;
      initialValues = { vin };
      setSearchValueByPill((prev) => ({ ...prev, [pill]: vin }));
    } else if (recallSearchType === 'LICENSE_PLATE') {
      const licensePlate = params.get('licensePlate');
      if (!licensePlate) return;
      formValues.licensePlate = licensePlate;
      const state = params.get('state');
      if (state) formValues.state = state;
      initialValues = { licensePlate, ...(state ? { state } : {}) };
      setSearchValueByPill((prev) => ({ ...prev, [pill]: licensePlate }));
    } else if (recallSearchType === 'YEAR_MAKE_MODEL') {
      const year = params.get('year');
      const make = params.get('make');
      const model = params.get('model');
      if (!year || !make || !model) return;
      formValues.year = year;
      formValues.make = make;
      formValues.model = model;
      const trim = params.get('trim');
      if (trim) formValues.trim = trim;
      initialValues = { year, make, model, ...(trim ? { trim } : {}) };
      setSearchValueByPill((prev) => ({
        ...prev,
        [pill]: [year, make, model].join(' '),
      }));
    } else if (
      recallSearchType === 'company_or_product' ||
      recallSearchType === 'recall_number'
    ) {
      const recallQuery = params.get('recallQuery');
      if (!recallQuery) return;
      formValues.query = recallQuery;
      setSearchValueByPill((prev) => ({ ...prev, [pill]: recallQuery }));
    } else {
      return;
    }

    setSavedDropdownByPill((prev) => ({
      ...prev,
      [pill]: { searchType: recallSearchType, initialValues },
    }));
    setSearchParams(buildSearchParams(pill, formValues));
  }, []);

  const includeSeverity =
    activePill !== 'Vehicle' &&
    (activePill !== 'Consumer Products' || expandedSection === 'fdaAndUsda');
  const sortOptions = useMemo(
    () => getRecallsSortOptions(includeSeverity),
    [includeSeverity]
  );

  useEffect(() => {
    if (!includeSeverity && sortOption === 'severity_desc') {
      setSortOption('date_desc');
    }
  }, [includeSeverity, sortOption]);

  const handleSearchValueChange = (value: string) => {
    setSearchValueByPill((prev) => ({ ...prev, [activePill]: value }));
  };

  const { data: newestData } = useNewestRecalls();
  const { data: vehicleData } = useVehicleRecalls(
    searchParams?.vehicleParams ?? null,
    { enabled: !!searchParams?.vehicleParams }
  );
  const { data: fdaData } = useFdaEnforcementRecalls(
    searchParams?.fdaParams ?? null,
    { enabled: !!searchParams?.fdaParams }
  );
  const { data: usdaData } = useUsdaRecalls(searchParams?.usdaParams ?? null, {
    enabled: !!searchParams?.usdaParams,
  });
  const { data: cpscData } = useCpscRecalls(searchParams?.cpscParams ?? null, {
    enabled: !!searchParams?.cpscParams,
  });

  const allFdaAndUsdaCards = useMemo(() => {
    const items = (newestData ?? []).filter(
      (item) =>
        item.source === 'fdaDrug' ||
        item.source === 'fdaDevice' ||
        item.source === 'fdaFood' ||
        item.source === 'usda'
    );
    return normalizeRecallsForCards(items);
  }, [newestData]);

  const allConsumerProductCards = useMemo(() => {
    const items = (newestData ?? []).filter((item) => item.source === 'cpsc');
    return normalizeRecallsForCards(items);
  }, [newestData]);

  const fdaAndUsdaRecallCards = useMemo(
    () => allFdaAndUsdaCards.slice(0, 3),
    [allFdaAndUsdaCards]
  );
  const consumerProductCards = useMemo(
    () => allConsumerProductCards.slice(0, 6),
    [allConsumerProductCards]
  );

  const isSearchActive = hasActiveSearch(searchParams);

  const activeResults = useMemo(() => {
    if (isSearchActive && searchParams) {
      return buildResultsPayload(searchParams, {
        vehicleData,
        fdaData,
        usdaData,
        cpscData,
      });
    }
    if (expandedSection === 'fdaAndUsda') {
      return {
        title: EXPANDED_SECTION_TITLES.fdaAndUsda,
        count: allFdaAndUsdaCards.length,
        recallType: '',
        cards: allFdaAndUsdaCards,
      };
    }
    if (expandedSection === 'cpsc') {
      return {
        title: EXPANDED_SECTION_TITLES.cpsc,
        count: allConsumerProductCards.length,
        recallType: '',
        cards: allConsumerProductCards,
      };
    }
    return null;
  }, [
    isSearchActive,
    searchParams,
    vehicleData,
    fdaData,
    usdaData,
    cpscData,
    expandedSection,
    allFdaAndUsdaCards,
    allConsumerProductCards,
  ]);

  const sortedCards = useMemo(
    () =>
      sortRecallsCards(
        activeResults?.cards ?? [],
        (sortOption || 'date_desc') as RecallsSortValue
      ),
    [activeResults?.cards, sortOption]
  );

  const {
    currentPage,
    totalPages,
    getPaginatedItems,
    goToPage,
    resetToFirstPage,
  } = usePagination<NormalizedRecallCard>({
    totalItems: sortedCards.length,
    itemsPerPage: RECALLS_PER_PAGE,
    initialPage: 1,
  });

  const pageCards = getPaginatedItems(sortedCards);
  const showPagination = sortedCards.length > RECALLS_PER_PAGE;
  const resetPage = resetToFirstPage;
  const setPage = goToPage;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      resetToFirstPage();
    }
  }, [currentPage, totalPages, resetToFirstPage, sortedCards.length]);

  const handleSubmit = (values: RecallsFormValues) => {
    const valuesWithQuery = { ...values, query: values.query ?? searchValue };
    setSearchParams(buildSearchParams(activePill, valuesWithQuery));
    resetPage();
  };

  const handlePillClick = (newPill: RecallsPill) => {
    if (newPill === activePill) {
      return;
    }
    setActivePill(newPill);
    setSearchParams(null);
    setExpandedSection(null);
    resetPage();
  };

  const handleSearchTypeChange = (searchType: string) => {
    setSavedDropdownByPill((prev) => ({
      ...prev,
      [activePill]: { searchType },
    }));
  };

  const handleCardClick = useCallback(
    (card: NormalizedRecallCard) => {
      const url = buildRecallReportUrl(card, searchParams?.vehicleParams);
      const returnTo = encodeURIComponent(
        location.pathname + window.location.search
      );
      navigate(`${url}${url.includes('?') ? '&' : '?'}returnTo=${returnTo}`);
    },
    [navigate, location.pathname, searchParams?.vehicleParams]
  );

  const handleViewMore = (section: ExpandedSection) => {
    setExpandedSection(section);
    resetPage();
  };

  const handleBackFromExpanded = () => {
    setExpandedSection(null);
    resetPage();
  };

  const handleBackFromSearch = () => {
    setSearchParams(null);
    resetPage();
  };

  return {
    activePill,
    searchValue,
    savedDropdownByPill,
    activeResults,
    expandedSection,
    fdaAndUsdaRecallCards,
    consumerProductCards,
    currentPage,
    totalPages,
    pageCards,
    showPagination,
    recallsPerPage: RECALLS_PER_PAGE,
    sortOption,
    setSortOption,
    sortOptions,
    handleSearchValueChange,
    handleSubmit,
    handlePillClick,
    handleSearchTypeChange,
    handleCardClick,
    handleViewMore,
    handleBackFromExpanded,
    handleBackFromSearch,
    setPage,
  };
}
